import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  AccountCard,
  AppShell,
  AssumeBar,
  Container,
  Icon,
  type IconName,
  MobileBar,
  NavItem,
  Sidebar,
  SidebarBrand,
} from '@sparkery/ui';
// initialsOf comes from contracts, not from a local copy: it is computed server-side
// for every table avatar, and a second rule here made the shell's monogram disagree
// with the Team list's for the same person.
import { initialsOf, type Permission } from '@sparkery/contracts';
import { useViewer } from '../../lib/viewer.tsx';
import './portal-shell.css';

/**
 * The navigation, derived from permissions alone.
 *
 * The prototype branched on `isPartnerUser` / `isSuperadmin` in six places. Deriving
 * from permissions means the nav and the API guards agree by construction: if a viewer
 * cannot call the endpoint, the item is not there to click — and an impersonated
 * session gets the partner's navigation for free, because it holds the partner's
 * permissions.
 */
interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
  permission: Permission;
}

const NAV: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', icon: 'dashboard', permission: 'dashboard:read' },
  { to: '/refer', label: 'Refer', icon: 'refer', permission: 'links:read' },
  { to: '/reporting', label: 'Reporting', icon: 'reporting', permission: 'reporting:read' },
  { to: '/admin/partners', label: 'Partners', icon: 'partners', permission: 'partners:read' },
  { to: '/assets', label: 'Assets', icon: 'assets', permission: 'assets:read' },
  // Absent for a fixed-fee partner: they hold no `statements:read`, so there is
  // nothing here to filter out — the seat never granted it.
  { to: '/statements', label: 'Statements', icon: 'statements', permission: 'statements:read' },
  { to: '/team', label: 'Team', icon: 'team', permission: 'team:read' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings', permission: 'partners:manage' },
  { to: '/profile', label: 'Profile', icon: 'profile', permission: 'profile:manage' },
];

export function PortalShell() {
  const { viewer, can, quitImpersonation, signOut } = useViewer();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const isAdmin = viewer.audience === 'sparkery' && !viewer.impersonation;
  const items = NAV.filter((entry) => can(entry.permission));

  return (
    <AppShell
      sidebar={
        <Sidebar
          brand={
            <SidebarBrand
              logoSrc="/assets/logo-mark.svg"
              label={isAdmin ? 'Partner Portal Admin' : 'Partner Portal'}
              tone={isAdmin ? 'admin' : 'partner'}
            />
          }
          footer={
            <AccountCard
              initials={initialsOf(viewer.name)}
              name={viewer.name}
              meta={isAdmin ? 'Sparkery · Admin' : (viewer.partner?.name ?? 'Partner')}
              metaTone={isAdmin ? 'admin' : 'muted'}
              onSignOut={signOut}
              signOutIcon={<Icon name="logout" size={18} />}
            />
          }
        >
          {items.map((entry) => {
            return (
              <NavItem
                key={entry.to}
                as={Link}
                href={entry.to}
                icon={<Icon name={entry.icon} size={20} />}
                label={entry.label}
                // Prefix match, so /admin/partners/:id keeps Partners lit.
                active={pathname === entry.to || pathname.startsWith(`${entry.to}/`)}
              />
            );
          })}
        </Sidebar>
      }
      mobileBar={({ onOpenNav }) => (
        <MobileBar
          logoSrc="/assets/logo-mark.svg"
          label="Partner Portal"
          onOpenNav={onOpenNav}
          menuIcon={<Icon name="menu" size={22} />}
        />
      )}
      banner={
        viewer.impersonation && viewer.partner ? (
          <AssumeBar
            label={`Viewing as ${viewer.partner.name}`}
            onQuit={quitImpersonation}
            icon={<Icon name="eye" size={13} color="var(--sense-cyan)" />}
          />
        ) : null
      }
    >
      <Container className="portalPage">
        <Outlet />
      </Container>
    </AppShell>
  );
}
